import { useReducer, useCallback, useRef, useEffect } from "react";
import { VideoStreamRendererView, Call } from "@azure/communication-calling";
import { useTranslation } from "react-i18next";

import {
  AudioTestState,
  DeviceTesterTestState,
  ResultsViewState,
  DeviceStatusDialogState,
  NullableAudioDeviceList,
  NullableVideoDeviceList,
  CameraDevices,
  AudioDevices,
} from "../../types/Device.types";
import { DeviceStatus } from "../results/results-view/ResultsView.types";
import { useServiceContext } from "../acs-service-provider/ServiceContextProvider";
import { usePlatformTypeContext } from "../platform-type-provider/PlatformTypeProvider";
import { UnsupportedBrowsers } from "../platform-type-provider/PlatformType.types";
import { Paths } from "../../types/paths";
import { DeviceTestResult } from "../results/results-view/ResultsView.types";

import {
  DeviceTesterAction,
  DeviceTesterActionType,
  DeviceTesterState,
} from "./Reducer.types";
import { DeviceTesterReducer } from "./DeviceTesterReducer";

export interface IDeviceState {
  deviceTesterState: DeviceTesterState;
  setVideoStreamingOnOff: () => Promise<void>;
  startAudioTest: () => Promise<Call | undefined>;
  setDeviceDetails: () => Promise<void>;
  setNetworkConnectionListener: () => void;
  checkBrowserSupported: () => void;
}

export interface IDeviceDispatch {
  deviceTesterDispatch: React.Dispatch<DeviceTesterAction>;
}

export const useDeviceTester: () => IDeviceState & IDeviceDispatch = () => {
  const [state, dispatch] = useReducer(
    DeviceTesterReducer,
    {
      isInfoFetched: false,
      isAcsTokenFetched: false,

      deviceSettings: {
        // Selected devices
        selectedCamera: undefined,
        selectedMicrophone: undefined,
        selectedSpeaker: undefined,

        // Available devices
        microphones: undefined,
        speakers: undefined,
        cameras: undefined,
        isCamOn: false,

        //Audio Video Access Permissions
        cameraAccess: false,
        micAccess: false,
      },

      // Track state of device Tester testing flow
      deviceTesterTestState: DeviceTesterTestState.RESULTS,
      audioTestState: AudioTestState.INITIATED,
      resultsViewState: ResultsViewState.INITIAL,
      deviceStatusDialogState: DeviceStatusDialogState.CLOSED,
      // Device test results
      microphoneResult: { status: DeviceStatus.UNTESTED },
      speakerResult: { status: DeviceStatus.UNTESTED },
      cameraResult: { status: DeviceStatus.UNTESTED },
      networkResult: { status: DeviceStatus.UNTESTED },
    },
    undefined
  );

  const videoStreamRendererView = useRef<
    VideoStreamRendererView | null | undefined
  >(undefined);
  const videoStreamRendererViewId = useRef<string | null | undefined>(
    undefined
  );
  const call = useRef<Call | undefined>(undefined);
  const serviceContainer = useServiceContext();
  const acsService = serviceContainer?.acsService;
  const { browser } = usePlatformTypeContext();

  const { t } = useTranslation();

  // Helper function to set the video stream based on the provided camera
  const setVideoStreamingOnOff = useCallback(async () => {
    let rendererView: VideoStreamRendererView | null | undefined =
      videoStreamRendererView.current;
    const rendererViewId: string | null | undefined =
      videoStreamRendererViewId.current;
    if (
      rendererView &&
      (!state.deviceSettings.isCamOn ||
        (rendererViewId &&
          state.deviceSettings.selectedCamera &&
          rendererViewId !== state.deviceSettings.selectedCamera?.id))
    ) {
      rendererView?.dispose();
      videoStreamRendererView.current = undefined;
      videoStreamRendererViewId.current = undefined;
      rendererView = undefined;
    }
    if (state.deviceSettings.isCamOn) {
      if (!rendererView && state.deviceSettings.selectedCamera) {
        rendererView = await acsService?.setCameraStreaming(
          state.deviceSettings.selectedCamera
        );

        if (rendererView) {
          videoStreamRendererView.current = rendererView;
          videoStreamRendererViewId.current =
            state.deviceSettings.selectedCamera.id;
        }
      }
      const container = document.getElementById("camElement");
      if (rendererView && container?.childElementCount === 0) {
        container.appendChild(rendererView.target);
      }
    }
  }, [
    acsService,
    state.deviceSettings.isCamOn,
    state.deviceSettings.selectedCamera,
  ]);

  // Helper function to start the audio (speaker/microphone) test given a speaker and microphone
  const startAudioTest = useCallback(async () => {
    call.current = await acsService?.startTestCall();
    return call.current;
  }, [acsService]);

  // Helper function to retrieve device info set preliminary results
  const setDeviceDetails = useCallback(async () => {
    const deviceInfo = await acsService?.getDeviceInfo();
    if (deviceInfo) {
      let newNetworkResult: DeviceTestResult;
      let newCameraResult: DeviceTestResult;
      let newMicrophoneResult: DeviceTestResult;
      let newSpeakerResult: DeviceTestResult;

      // // Udpate the device info state
      dispatch({
        type: DeviceTesterActionType.SET_DEVICE_INFO,
        deviceInfo: deviceInfo,
      });

      // If network is online set as working
      const connected = navigator.onLine;
      if (connected) {
        newNetworkResult = {
          status: DeviceStatus.WORKING,
        };
      } else {
        newNetworkResult = {
          status: DeviceStatus.FAILED,
          detail: t("NETWORK_FAILURE"),
        };
      }

      dispatch({
        type: DeviceTesterActionType.SET_NETWORK_RESULT,
        networkResult: newNetworkResult,
      });

      // If no default device is present, assume that there isn't a functioning device available
      if (!deviceInfo.defaultCamera) {
        newCameraResult = {
          status: DeviceStatus.MISSING,
          detail: t("CAMERA_NOT_FOUND"),
        };
      } else {
        newCameraResult = { status: DeviceStatus.WORKING };
      }

      dispatch({
        type: DeviceTesterActionType.SET_CAMERA_RESULT,
        cameraResult: newCameraResult,
      });

      if (!deviceInfo.defaultMicrophone) {
        newMicrophoneResult = {
          status: DeviceStatus.MISSING,
          detail: t("MIC_NOT_FOUND"),
        };
      } else {
        newMicrophoneResult = {
          status: DeviceStatus.WORKING,
        };
      }

      dispatch({
        type: DeviceTesterActionType.SET_MICROPHONE_RESULT,
        microphoneResult: newMicrophoneResult,
      });

      if (!deviceInfo.defaultSpeaker) {
        if (!deviceInfo.isSpeakerSelectionAvailable) {
          newSpeakerResult = {
            status: DeviceStatus.WORKING,
          };
        } else {
          newSpeakerResult = {
            status: DeviceStatus.MISSING,
            detail: t("SPEAKER_NOT_FOUND"),
          };
        }
      } else {
        newSpeakerResult = {
          status: DeviceStatus.WORKING,
        };
      }

      dispatch({
        type: DeviceTesterActionType.SET_SPEAKER_RESULT,
        speakerResult: newSpeakerResult,
      });
    }
  }, [acsService, t]);

  const setNetworkConnectionListener = useCallback(() => {
    const connected = navigator.onLine;
    if (!connected) {
      dispatch({
        type: DeviceTesterActionType.SET_NETWORK_RESULT,
        networkResult: {
          status: DeviceStatus.FAILED,
          detail: t("NETWORK_FAILURE"),
        },
      });
    }
    // Update device status in case of network outage but not for reconnection. Any downtime
    // should be reported to user
    window.addEventListener("offline", () =>
      dispatch({
        type: DeviceTesterActionType.SET_NETWORK_RESULT,
        networkResult: {
          status: DeviceStatus.FAILED,
          detail: t("NETWORK_FAILURE"),
        },
      })
    );
  }, []);

  // Helper function to redirect to browser not supported page if not supported
  const checkBrowserSupported = useCallback(() => {
    if (browser === UnsupportedBrowsers.Firefox) {
      window.location.pathname = Paths.BrowserError;
    }
  }, [browser]);

  const hangUpCall = useCallback(async () => {
    if (call.current?.id) {
      await call.current?.hangUp();
      call.current = undefined;
    }
  }, []);

  // Callback to update the video device lists upon event fired
  const updateVideoDevices = useCallback(
    (newCameraDevices: NullableVideoDeviceList) => {
      // if the selected camera is no longer available, set it to the default camera
      if (
        newCameraDevices &&
        newCameraDevices.length > 0 &&
        newCameraDevices.find(
          (camera) => camera?.id === state.deviceSettings.selectedCamera?.id
        ) === undefined
      ) {
        dispatch({
          type: DeviceTesterActionType.SET_SELECTED_CAMERA,
          cameraId: newCameraDevices[0].id,
        });
      }

      const newCameras: CameraDevices = {
        cameras: newCameraDevices ?? state.deviceSettings.cameras,
      };
      dispatch({
        type: DeviceTesterActionType.UPDATE_VIDEO_DEVICES,
        newCameraDevices: newCameras,
      });
    },
    [state.deviceSettings.cameras, state.deviceSettings.selectedCamera]
  );

  // Callback to udpate the audio device lists upon event fired
  const updateAudioDevices = useCallback(
    (
      newMicrophoneDevices: NullableAudioDeviceList,
      newSpeakerDevices: NullableAudioDeviceList
    ) => {
      // If the selected microphone is no longer available, set it to the default microphone
      if (
        newMicrophoneDevices &&
        newMicrophoneDevices.length > 0 &&
        newMicrophoneDevices.find(
          (microphone) =>
            microphone?.id === state.deviceSettings.selectedMicrophone?.id
        ) === undefined
      ) {
        dispatch({
          type: DeviceTesterActionType.SET_SELECTED_MICROPHONE,
          microphoneId: newMicrophoneDevices[0].id,
        });
      }

      // If the selected speaker is no longer available, set it to the default speaker
      if (
        newSpeakerDevices &&
        newSpeakerDevices.length > 0 &&
        newSpeakerDevices.find(
          (speaker) => speaker?.id === state.deviceSettings.selectedSpeaker?.id
        ) === undefined
      ) {
        dispatch({
          type: DeviceTesterActionType.SET_SELECTED_SPEAKER,
          speakerId: newSpeakerDevices[0].id,
        });
      }

      const newDeviceInfo: AudioDevices = {
        microphones: newMicrophoneDevices ?? state.deviceSettings.microphones,
        speakers: newSpeakerDevices ?? state.deviceSettings.speakers,
      };

      dispatch({
        type: DeviceTesterActionType.UPDATE_AUDIO_DEVICES,
        newAudioDevices: newDeviceInfo,
      });
    },
    [
      state.deviceSettings.microphones,
      state.deviceSettings.speakers,
      state.deviceSettings.selectedMicrophone,
      state.deviceSettings.selectedSpeaker,
    ]
  );

  // Subscribe to the device change event
  // the third parameter is to unsubscribe the event when the component unmounts
  useEffect(() => {
    acsService?.manageDeviceUpdateSubscription(
      updateVideoDevices,
      updateAudioDevices,
      false
    );
    return () => {
      acsService?.manageDeviceUpdateSubscription(
        updateVideoDevices,
        updateAudioDevices,
        true
      );
    };
  }, [acsService, updateVideoDevices, updateAudioDevices]);

  // hang up the audio call if not in audio state
  useEffect(() => {
    if (state.deviceTesterTestState !== DeviceTesterTestState.AUDIO) {
      hangUpCall();
    }
  }, [state.deviceTesterTestState, hangUpCall]);

  return {
    //state interface
    deviceTesterState: state,
    setVideoStreamingOnOff,
    startAudioTest,
    setDeviceDetails,
    setNetworkConnectionListener,
    checkBrowserSupported,
    //dispatch interface
    deviceTesterDispatch: dispatch,
  };
};
